import { useMemo, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import CompanyMultiSelect from '@/components/CompanyMultiSelect';

const COLORS = [
  'hsl(220, 70%, 50%)',
  'hsl(160, 60%, 45%)',
  'hsl(30, 80%, 55%)',
  'hsl(0, 72%, 51%)',
  'hsl(270, 60%, 55%)',
  'hsl(190, 70%, 45%)',
  'hsl(340, 65%, 50%)',
  'hsl(45, 85%, 50%)',
  'hsl(140, 50%, 40%)',
  'hsl(300, 50%, 50%)',
  'hsl(200, 80%, 60%)',
  'hsl(15, 75%, 50%)',
  'hsl(250, 55%, 55%)',
  'hsl(170, 65%, 40%)',
  'hsl(60, 70%, 45%)',
  'hsl(320, 60%, 55%)',
  'hsl(100, 50%, 45%)',
  'hsl(210, 60%, 60%)',
  'hsl(350, 70%, 55%)',
  'hsl(120, 45%, 50%)'
];

const formatDate = (dateStr) => {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;

  const sorted = [...payload].filter((p) => p.value > 0).sort((a, b) => b.value - a.value);

  return (
    <div className="rounded-md border bg-popover p-3 text-popover-foreground shadow-md">
      <p className="mb-1.5 text-sm font-medium">{formatDate(label)}</p>
      {sorted.map((entry) => (
        <div key={entry.dataKey} className="flex items-center gap-2 text-xs">
          <div className="h-2 w-2 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="text-muted-foreground">{entry.name}:</span>
          <span className="font-medium">{entry.value.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
};

const CompanyActivityChart = ({ data }) => {
  const [selectedUids, setSelectedUids] = useState(null);

  const { companies, chartData } = useMemo(() => {
    if (!data || data.length === 0) return { companies: [], chartData: [] };

    const companyMap = {};
    data.forEach((row) => {
      if (!companyMap[row.company_uid]) {
        companyMap[row.company_uid] = { uid: row.company_uid, name: row.company_name, total: 0 };
      }
      companyMap[row.company_uid].total += row.request_count;
    });

    const sortedCompanies = Object.values(companyMap).sort((a, b) => b.total - a.total);

    const dateMap = {};
    data.forEach((row) => {
      const dateKey = typeof row.date === 'string' ? row.date.split('T')[0] : new Date(row.date).toISOString().split('T')[0];
      if (!dateMap[dateKey]) {
        dateMap[dateKey] = { date: dateKey };
      }
      dateMap[dateKey][row.company_uid] = row.request_count;
    });

    const sorted = Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date));

    return { companies: sortedCompanies, chartData: sorted };
  }, [data]);

  const activeUids = useMemo(() => {
    if (!selectedUids) return new Set(companies.map((c) => c.uid));
    return selectedUids;
  }, [selectedUids, companies]);

  const visibleCompanies = useMemo(() => {
    return companies.filter((c) => activeUids.has(c.uid));
  }, [companies, activeUids]);

  const colorMap = useMemo(() => {
    const map = {};
    companies.forEach((c, i) => {
      map[c.uid] = COLORS[i % COLORS.length];
    });
    return map;
  }, [companies]);

  const toggleCompany = (uid) => {
    const next = new Set(activeUids);
    if (next.has(uid)) {
      next.delete(uid);
    } else {
      next.add(uid);
    }
    setSelectedUids(next.size === companies.length ? null : next);
  };

  if (!data || data.length === 0) return null;

  return (
    <Card className="flex flex-col">
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <div>
            <CardTitle>Company Activity</CardTitle>
            <CardDescription>Daily request volume by company</CardDescription>
          </div>
          <CompanyMultiSelect companies={companies} selectedUids={selectedUids} onChange={setSelectedUids} colorMap={colorMap} />
        </div>
      </CardHeader>
      <CardContent className="flex-1">
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} tickFormatter={formatDate} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              {visibleCompanies.map((company) => (
                <Line key={company.uid} type="monotone" dataKey={company.uid} name={company.name} stroke={colorMap[company.uid]} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
          {companies.map((company) => {
            const isActive = activeUids.has(company.uid);
            return (
              <button
                key={company.uid}
                className={`flex items-center gap-1.5 text-xs transition-colors hover:text-foreground ${isActive ? 'text-foreground' : 'text-muted-foreground/40'}`}
                onClick={() => toggleCompany(company.uid)}
              >
                <div className="h-2.5 w-2.5 rounded-full transition-opacity" style={{ backgroundColor: colorMap[company.uid], opacity: isActive ? 1 : 0.25 }} />
                {company.name}
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default CompanyActivityChart;
